
import { ReactNode } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { LoginForm } from "@/components/auth/LoginForm";
import { Button } from "@/components/ui/button";
import { LogOut, User, Settings, Home, Menu, X } from "lucide-react";
import { useState } from "react";

interface AdminLayoutProps {
  children: ReactNode;
}

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { user, session, profile, loading, profileError, signOut, isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  console.log('AdminLayout render:', { 
    user: user?.email, 
    profile: profile?.role, 
    loading, 
    profileError,
    hasSession: !!session 
  });

  // Show loading while authentication and profile are being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teedee-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login form if no user or session is authenticated
  if (!user || !session) {
    console.log('No user or session, showing login form');
    return <LoginForm />;
  }

  // Show profile error if there's an issue loading the profile
  if (profileError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-blue/20 via-white to-bobo-green/20">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Profile Error</h1>
          <p className="text-gray-600 mb-4">{profileError}</p>
          <Button onClick={signOut} variant="outline">
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  // Show loading if profile is still null but no error
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teedee-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Check admin permissions only after we have a profile
  if (!isAdmin()) {
    console.log('User is not admin:', profile.role);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-blue/20 via-white to-bobo-green/20">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">You don't have admin permissions.</p>
          <Button onClick={signOut} variant="outline">
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  console.log('Rendering admin layout for:', profile.email, profile.role);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Left side - Logo and title */}
            <div className="flex items-center space-x-2 md:space-x-4 min-w-0 flex-1">
              <h1 className="text-lg md:text-xl font-bold text-teedee-blue truncate">
                Bobo & Teedee Admin
              </h1>
              {profile?.role === 'super_admin' && (
                <span className="hidden xs:inline px-2 py-1 bg-golden-orange text-white text-xs rounded-full whitespace-nowrap">
                  Super Admin
                </span>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-600 hover:text-teedee-blue"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Go to Home
                </Button>
              </Link>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <User className="h-4 w-4" />
                <span className="truncate max-w-32">{profile?.first_name || profile?.email}</span>
              </div>
              <Button
                onClick={signOut}
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-red-600"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-teedee-blue hover:bg-sky-blue/20 p-2"
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle admin menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-3">
              <div className="space-y-2">
                {/* User info */}
                <div className="flex items-center space-x-2 px-2 py-2 text-sm text-gray-600">
                  <User className="h-4 w-4" />
                  <span className="truncate">{profile?.first_name || profile?.email}</span>
                  {profile?.role === 'super_admin' && (
                    <span className="px-2 py-1 bg-golden-orange text-white text-xs rounded-full">
                      Super Admin
                    </span>
                  )}
                </div>
                
                {/* Navigation items */}
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-gray-600 hover:text-teedee-blue hover:bg-sky-blue/20"
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Go to Home
                  </Button>
                </Link>
                
                <Button
                  onClick={() => {
                    signOut();
                    setIsMobileMenuOpen(false);
                  }}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-gray-600 hover:text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Admin Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};
